import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import "./address-map-picker.css";

// Fix default icon paths for Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

export const AddressMapPicker = ({ handleAddressPicked, onCancel }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);

  const [selectedAddress, setSelectedAddress] = useState(null);
  const [userLocation, setUserLocation] = useState(null);

  const IP_GEOLOCATION_API_KEY = "0a0dfe0a8b1742b7b05b43dd6b6bf187";

  useEffect(() => {
    // Get user location by IP
    fetch(`https://api.ipgeolocation.io/ipgeo?apiKey=${IP_GEOLOCATION_API_KEY}`)
      .then((res) => res.json())
      .then((data) => {
        setUserLocation({
          lat: parseFloat(data.latitude),
          lng: parseFloat(data.longitude),
        });
      })
      .catch((err) => {
        console.error("Failed to fetch user location", err);
        setUserLocation({ lat: 52.52, lng: 13.405 }); // Berlin fallback
      });
  }, []);

  useEffect(() => {
    if (userLocation && !mapInstanceRef.current && mapRef.current) {
      // Initialize Leaflet map
      const map = L.map(mapRef.current).setView([userLocation.lat, userLocation.lng], 14);

      // Add OSM tile layer
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "&copy; OpenStreetMap contributors",
      }).addTo(map);

      // Add marker
      const marker = L.marker([userLocation.lat, userLocation.lng], {
        draggable: true,
      }).addTo(map);

      markerRef.current = marker;

      // Reverse geocode initial position
      reverseGeocode(userLocation);

      // Marker drag event
      marker.on("dragend", () => {
        const { lat, lng } = marker.getLatLng();
        reverseGeocode({ lat, lng });
      });

      // Click on map to move marker
      map.on("click", (e) => {
        marker.setLatLng(e.latlng);
        reverseGeocode(e.latlng);
      });

      mapInstanceRef.current = map;
    }
  }, [userLocation]);

  const reverseGeocode = async ({ lat, lng }) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1`
      );
      const data = await response.json();
      if (data && data.address) {
        const addr = data.address;
        const addressObj = {
          street: addr.road || "",
          houseNumber: addr.house_number || "",
          postalCode: addr.postcode || "",
          city: addr.city || addr.town || addr.village || "",
          state: addr.state || "",
          countryName: addr.country || "",
          countryCode: addr.country_code ? addr.country_code.toUpperCase() : "",
          longitude: lng,
          latitude: lat,
          label: data.display_name,
          county: addr.county || "",
          district: addr.suburb || addr.neighbourhood || "",
        };

        setSelectedAddress(addressObj);
      }
    } catch (error) {
      console.error("Failed to reverse geocode with Nominatim", error);
    }
  };

  const confirmAddress = () => {
    if (selectedAddress) {
      handleAddressPicked(selectedAddress);
      cleanupMap();
    }
  };

  const cleanupMap = () => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }
    onCancel();
  };

  return (
    <div className="map-picker-modal-overlay">
      <div className="map-picker-modal">
        <span className="close-map-picker-icon" onClick={cleanupMap}>
          &times;
        </span>
        <h2 className="map-picker-modal-title">Choose Your Address</h2>
        <div className="address-map-picker">
          {userLocation ? (
            <>
              <div
                ref={mapRef}
                style={{
                  width: "100%",
                  height: "400px",
                  border: "1px solid #ccc",
                }}
              />
              {selectedAddress && (
                <div className="selected-address-container">
                  <p className="selected-address">
                    <span className="address-line">
                      <i className="icon-flag" /> {selectedAddress.street}{" "}
                    </span>
                    <span className="address-line">
                      {selectedAddress.postalCode} {selectedAddress.city},{" "}
                      {selectedAddress.state}
                    </span>
                    <span className="address-line">
                      {selectedAddress.countryName}
                    </span>
                  </p>
                </div>
              )}
              <div className="address-map-picker-buttons">
                <button
                  className="btn btn-secondary"
                  onClick={confirmAddress}
                  disabled={!selectedAddress}
                  style={{
                    marginRight: "10px",
                    opacity: selectedAddress ? "1" : "0.5",
                    cursor: selectedAddress ? "pointer" : "not-allowed",
                  }}
                >
                  Confirm Address
                </button>
              </div>
            </>
          ) : (
            <p>Loading map...</p>
          )}
        </div>
      </div>
    </div>
  );
};
